import React from 'react';
import './DeviceCard.css';

export interface Device {
  id: string;
  name: string;
  type: 'light' | 'thermostat' | 'fan' | 'switch' | 'lock' | 'camera';
  room: string;
  isVirtual?: boolean;
  state: {
    power?: 'on' | 'off';
    brightness?: number | string;
    temperature?: number | string;
    targetTemperature?: number | string;
    speed?: number | string;
    locked?: boolean | string;
    color?: string;
    mode?: string;
  };
}

interface DeviceCardProps {
  device: Device;
  onTogglePower: (deviceId: string) => void;
  onUpdateValue: (deviceId: string, property: string, value: any) => void;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device, onTogglePower, onUpdateValue }) => {
  const { id, name, type, room, state } = device;
  
  // Determine if device is on or off
  const isOn = state.power === 'on';
  
  // Handle power toggle
  const handleToggle = () => {
    onTogglePower(id);
  };
  
  // Handle brightness change
  const handleBrightnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const brightness = parseInt(e.target.value);
    onUpdateValue(id, 'brightness', brightness);
  };
  
  // Handle temperature change
  const handleTemperatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const temperature = parseInt(e.target.value);
    onUpdateValue(id, 'targetTemperature', temperature);
  };
  
  // Handle fan speed change
  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const speed = parseInt(e.target.value);
    onUpdateValue(id, 'speed', speed);
  };
  
  // Render controls based on device type
  const renderControls = () => {
    switch (type) {
      case 'light':
        return (
          <div className="device-controls">
            <label className="switch">
              <input 
                type="checkbox" 
                checked={isOn} 
                onChange={handleToggle}
              />
              <span className="slider round"></span>
            </label>
            
            {isOn && state.brightness !== undefined && (
              <div className="slider-control">
                <input 
                  type="range" 
                  min="1" 
                  max="100" 
                  value={typeof state.brightness === 'string' ? parseInt(state.brightness) : state.brightness} 
                  onChange={handleBrightnessChange}
                  className="brightness-slider"
                />
                <span className="slider-value">{state.brightness}%</span>
              </div>
            )}
          </div>
        );
        
      case 'thermostat':
        return (
          <div className="device-controls thermostat-controls">
            <div className="temperature-display">
              <div className="current-temp">
                {state.temperature !== undefined ? `${state.temperature}°F` : '--'}
              </div>
              <div className="target-temp">
                Target: {state.targetTemperature !== undefined ? `${state.targetTemperature}°F` : '--'}
              </div>
            </div>
            
            <div className="temperature-controls">
              <button 
                className="temp-button decrease"
                onClick={() => state.targetTemperature !== undefined && 
                  onUpdateValue(id, 'targetTemperature', typeof state.targetTemperature === 'string' ? 
                    parseInt(state.targetTemperature as string) - 1 : state.targetTemperature - 1)}
              >
                -
              </button>
              <input 
                type="range" 
                min="60" 
                max="90" 
                value={typeof state.targetTemperature === 'string' ? parseInt(state.targetTemperature as string) : (state.targetTemperature || 72)} 
                onChange={handleTemperatureChange}
                className="temperature-slider"
              />
              <button 
                className="temp-button increase"
                onClick={() => state.targetTemperature !== undefined && 
                  onUpdateValue(id, 'targetTemperature', typeof state.targetTemperature === 'string' ? 
                    parseInt(state.targetTemperature as string) + 1 : state.targetTemperature + 1)}
              >
                +
              </button>
            </div>
            
            <div className="mode-controls">
              <button 
                className={`mode-button ${state.mode === 'heat' ? 'active' : ''}`}
                onClick={() => onUpdateValue(id, 'mode', 'heat')}
              >
                Heat
              </button>
              <button 
                className={`mode-button ${state.mode === 'cool' ? 'active' : ''}`}
                onClick={() => onUpdateValue(id, 'mode', 'cool')}
              >
                Cool
              </button>
              <button 
                className={`mode-button ${state.mode === 'auto' ? 'active' : ''}`}
                onClick={() => onUpdateValue(id, 'mode', 'auto')}
              >
                Auto
              </button>
            </div>
          </div>
        );
        
      case 'fan':
        return (
          <div className="device-controls">
            <label className="switch">
              <input 
                type="checkbox" 
                checked={isOn} 
                onChange={handleToggle}
              />
              <span className="slider round"></span>
            </label>
            
            {isOn && state.speed !== undefined && (
              <div className="slider-control">
                <input 
                  type="range" 
                  min="1" 
                  max="3" 
                  value={typeof state.speed === 'string' ? parseInt(state.speed as string) : state.speed} 
                  onChange={handleSpeedChange}
                  className="speed-slider"
                  step="1"
                />
                <span className="slider-value">
                  {state.speed === 1 ? 'Low' : state.speed === 2 ? 'Medium' : 'High'}
                </span>
              </div>
            )}
          </div>
        );
        
      case 'lock':
        return (
          <div className="device-controls lock-controls">
            <button 
              className={`lock-button ${state.locked ? 'locked' : 'unlocked'}`}
              onClick={() => onUpdateValue(id, 'locked', !state.locked)}
            >
              {state.locked ? 'Locked' : 'Unlocked'}
            </button>
          </div>
        );
        
      default:
        return (
          <div className="device-controls">
            <label className="switch">
              <input 
                type="checkbox" 
                checked={isOn} 
                onChange={handleToggle}
              />
              <span className="slider round"></span>
            </label>
          </div>
        );
    }
  };
  
  return (
    <div className={`device-card ${type} ${isOn ? 'on' : 'off'} ${device.isVirtual ? 'virtual' : ''}`}>
      <div className="device-header">
        <h3 className="device-name">{name}</h3>
        <span className="device-room">{room}</span>
      </div>
      
      <div className="device-status">
        <span className={`status-indicator ${isOn ? 'on' : 'off'}`}></span>
        <span className="status-text">{isOn ? 'On' : 'Off'}</span>
      </div>
      
      {renderControls()}
    </div>
  );
};

export default DeviceCard;